import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import dayjs from "dayjs"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"
import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterPrecosCalculadosAberturaTable = (props) => {
    const datalakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)

        datalakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        }).catch((err) => {
            props.loading(false)
            props.callbackError(err)
        }).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            "CETIP": "N/A",
            "DataReferencia": dayjs().format(DATE_FORMAT),
            "Indexador": "N/A",
            "IDRiskfree": 0,
            "SpreadCurvaRating": 0,
            "SpreadRelativo": 0,
            "SpreadCredito": 0,
            "TIRAccrual": 0,
            "TaxaTIR": 0,
            "TaxaMTM": 0,
            "PUMTM": 0,
            "DurationAccrual": 0,
            "DurationMTM": 0,
            "DurationAno": 0,
            "PUPAR": 0,
            "PercentualPUPAR": 0,
            "LOGERRO": ""
        }

        props.loading(true)

        return datalakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)

            console.error("Erro ao criar registro:")
            console.error("Mensagem:", err?.message)
            console.error("Response:", err?.response)
            console.error("Response data:", err?.response?.data)
            console.error("Status:", err?.response?.status)

            throw err
        })
    }

    const updateRow = async (newRow) => {
        const {
            ID,
            CETIP,
            DataReferencia,
            Indexador,
            IDRiskfree,
            SpreadCurvaRating,
            SpreadRelativo,
            SpreadCredito,
            TIRAccrual,
            TaxaTIR,
            TaxaMTM,
            PUMTM,
            DurationAccrual,
            DurationMTM,
            DurationAno,
            PUPAR,
            PercentualPUPAR,
            LOGERRO
        } = newRow

        const body = {
            "CETIP": CETIP,
            "DataReferencia": DataReferencia,
            "Indexador": Indexador,
            "IDRiskfree": IDRiskfree,
            "SpreadCurvaRating": SpreadCurvaRating,
            "SpreadRelativo": SpreadRelativo,
            "SpreadCredito": SpreadCredito,
            "TIRAccrual": TIRAccrual,
            "TaxaTIR": TaxaTIR,
            "TaxaMTM": TaxaMTM,
            "PUMTM": PUMTM,
            "DurationAccrual": DurationAccrual,
            "DurationMTM": DurationMTM,
            "DurationAno": DurationAno,
            "PUPAR": PUPAR,
            "PercentualPUPAR": PercentualPUPAR,
            "LOGERRO": LOGERRO
        }

        props.loading(true)

        return datalakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList
            .map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`)
            .join('&')

        props.loading(true)

        datalakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)

        datalakeService.paginate(endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80
        },
        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: 1,
            minWidth: 150,
            editable: true
        },
        {
            field: 'DataReferencia',
            headerName: 'DataReferencia',
            flex: 1,
            minWidth: 150,
            typeComponent: 'date',
            editable: true,
            renderCell: (params) =>
                params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
        },
        {
            field: 'Indexador',
            headerName: 'Indexador',
            flex: 1,
            minWidth: 150,
            editable: true
        },
        {
            field: 'IDRiskfree',
            headerName: 'IDRiskfree',
            flex: 1,
            minWidth: 150,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'SpreadCurvaRating',
            headerName: 'SpreadCurvaRating',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'SpreadRelativo',
            headerName: 'SpreadRelativo',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'SpreadCredito',
            headerName: 'SpreadCredito',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'TIRAccrual',
            headerName: 'TIRAccrual',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'TaxaTIR',
            headerName: 'TaxaTIR',
            flex: 1,
            minWidth: 150,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'TaxaMTM',
            headerName: 'TaxaMTM',
            flex: 1,
            minWidth: 150,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'PUMTM',
            headerName: 'PUMTM',
            flex: 1,
            minWidth: 150,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'DurationAccrual',
            headerName: 'DurationAccrual',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'DurationMTM',
            headerName: 'DurationMTM',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'DurationAno',
            headerName: 'DurationAno',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'PUPAR',
            headerName: 'PUPAR',
            flex: 1,
            minWidth: 150,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'PercentualPUPAR',
            headerName: 'PercentualPUPAR',
            flex: 1,
            minWidth: 180,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'LOGERRO',
            headerName: 'LOGERRO',
            flex: 1,
            minWidth: 250,
            editable: true
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                updateRow={updateRow}
                pagination={pagination}
                onChangePage={changePage}
                filterFunction={onFilter}
                customFilter={true}
            />
        </div>
    )
}

ParameterPrecosCalculadosAberturaTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterPrecosCalculadosAberturaTable